import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormControl, InputLabel, MenuItem } from '@material-ui/core';

import { ContentModal, Divider, SectionHeader } from '@Components';
import { Button, SelectInput } from '@Components/common';
import { Project } from '@Types';
import { useRouter } from '@Helpers/useRouter';
import { useCreateMatchmakingApplyFundNonProfitLink } from '@Hooks/Api/useCreateMatchmakingApplyFundNonProfitLink';
import { ErrorCode } from '@Types';
import { Routes } from '@App/paths';
import * as Sentry from '@sentry/react';
import styles from './ApplyToFundModal.module.scss';
import { useNotificationsContext } from '@Contexts/NotificationsContext';
import { useCreateMatchmakingApplyFundProjectLink } from '@Hooks/Api/useCreateMatchmakingApplyFundProjectLink';

interface ApplyToFundModalProps {
  isOpen: boolean;
  onClose: () => void;
  fundId: number;
  editableProjects: Project[];
  onApplySuccess: () => void;
}

export const ApplyToFundModal: React.FC<ApplyToFundModalProps> = ({
  isOpen,
  onClose,
  fundId,
  editableProjects,
  onApplySuccess,
}) => {
  const { t } = useTranslation('fund');
  const { t: tCommon } = useTranslation('common');
  const { push } = useRouter();
  const { success: successNotification, error: errorNotification } = useNotificationsContext();
  const [createMatchmakingApplyFundNonProfitLink] = useCreateMatchmakingApplyFundNonProfitLink();
  const [createMatchmakingApplyFundProjectLink] = useCreateMatchmakingApplyFundProjectLink();
  const [selectedProjectId, setSelectedProjectId] = useState<number | ''>('');
  const [isApplying, setIsApplying] = useState(false);

  const handleCreateNewProject = () => {
    createMatchmakingApplyFundNonProfitLink(fundId)
      .then(() => {
        push(Routes.PROJECT_CREATE);
      })
      .catch((error) => {
        if (error.status === 409 && error.data?.error === ErrorCode.LINK_ALREADY_EXISTS) {
          // Link already exists - that's fine, proceed anyway
          push(Routes.PROJECT_CREATE);
        } else {
          // Log unexpected errors to Sentry
          Sentry.captureException(error);
        }
      });
  };

  const handleApplyWithProject = () => {
    if (!selectedProjectId) return;
    setIsApplying(true);
    // First create the non-profit link, then create the project link
    createMatchmakingApplyFundNonProfitLink(fundId)
      .then(() => createMatchmakingApplyFundProjectLink(fundId, selectedProjectId))
      .then((response) => {
        // Success - both operations completed
        setIsApplying(false);
        successNotification(t('details_page.apply_modal.success'));
        onApplySuccess();
        // If there's a project_fund_match_id, redirect to additional questions page
        if (response?.data?.project_fund_match_id) {
          push(
            Routes.FUND_ADDITIONAL_QUESTIONS(
              selectedProjectId as number,
              response.data.project_fund_match_id,
            ),
          );
        } else {
          onClose();
        }
      })
      .catch((error) => {
        if (error.status === 409 && error.data?.error === ErrorCode.LINK_ALREADY_EXISTS) {
          // Non-profit link already exists - proceed to make Project link
          createMatchmakingApplyFundProjectLink(fundId, selectedProjectId)
            .then((response) => {
              // Success - project link created
              setIsApplying(false);
              successNotification(t('details_page.apply_modal.success'));
              onApplySuccess();
              // If there's a project_fund_match_id, redirect to additional questions page
              if (response?.data?.project_fund_match_id) {
                push(
                  Routes.FUND_ADDITIONAL_QUESTIONS(
                    selectedProjectId as number,
                    response.data.project_fund_match_id,
                  ),
                );
              } else {
                onClose();
              }
            })
            .catch((projectLinkError) => {
              // Error creating project link
              setIsApplying(false);
              Sentry.captureException(projectLinkError);
              errorNotification(tCommon('errors.generic_error'));
            });
        } else {
          // Log unexpected errors to Sentry and show error to user
          setIsApplying(false);
          Sentry.captureException(error);
          errorNotification(tCommon('errors.generic_error'));
        }
      });
  };

  return (
    <ContentModal isOpen={isOpen} onClose={onClose} title={t('details_page.apply_modal.title')}>
      <div>
        <p>{t('details_page.apply_modal.description')}</p>
        <div>
          {/* <SectionHeader title={t('details_page.apply_modal.create_new_project')} /> */}
          <Button buttonType="primary" onClick={handleCreateNewProject} fullWidth>
            {t('details_page.apply_modal.create_new_project_button')}
          </Button>
        </div>

        <div className={styles.orSeparator}>
          <Divider />
          <p>{t('details_page.apply_modal.or_separator')}</p>
          <Divider />
        </div>

        {editableProjects.length > 0 && (
          <div className={styles.projectSelection}>
            <SectionHeader title={t('details_page.apply_modal.select_an_existing_project')} />
            <FormControl fullWidth>
              <InputLabel>{t('details_page.apply_modal.select_project')}</InputLabel>
              <SelectInput
                value={selectedProjectId}
                onChange={(e) => setSelectedProjectId(e.target.value as number | '')}
                label={t('details_page.apply_modal.select_project')}
              >
                <MenuItem value="">
                  {t('details_page.apply_modal.select_project_placeholder')}
                </MenuItem>
                {editableProjects.map((project) => (
                  <MenuItem key={project.id} value={project.id}>
                    {project.details.name}
                  </MenuItem>
                ))}
              </SelectInput>
            </FormControl>

            <div>
              <Button
                buttonType="primary"
                onClick={handleApplyWithProject}
                disabled={isApplying || !selectedProjectId}
                fullWidth
              >
                {isApplying
                  ? t('details_page.apply_modal.applying')
                  : t('details_page.apply_modal.apply_with_project')}
              </Button>
            </div>
          </div>
        )}
      </div>
    </ContentModal>
  );
};
